import React from 'react';
import { Provider } from 'react-redux';
import PropTypes from 'prop-types';
import styled, { ThemeProvider } from 'styled-components';
import IntlProvider, { FormattedMessage } from 'i18n';
import Heart from 'components/Heart';
import Stars from 'components/ratings/Stars';
import Score from 'components/ratings/Score';
import CarouselHotelImages from 'components/carousel/CarouselHotelImages';
import LinkIntercept from 'components/linkIntercept/LinkIntercept';
import flexbox from 'styles/utils/flexbox';
import Dates from 'components/packageCard/components/dates/Dates';
import MapMarkerHotelPrice from './MapMarkerHotelPrice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const propTypes = {
  slug: PropTypes.string.isRequired,
  hotelImages: PropTypes.array.isRequired,
  hotelName: PropTypes.string.isRequired,
  hotelStars: PropTypes.number.isRequired,
  hotelScore: PropTypes.number.isRequired,
  departure: PropTypes.string,
  onClickViewInList: PropTypes.func,
  simple: PropTypes.bool,
};

function MapMarkerPopupComponent({
  store,
  theme,
  slug,
  hotelImages,
  hotelName,
  hotelStars,
  hotelScore,
  hotelPrice,
  dates,
  onClickViewInList,
  simple,
  onClickAddHearted,
  onClickRemoveHearted,
  departure,
}) {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <IntlProvider namespace={'app'} forMapboxMap>
          <Root>
            <HeartWrap>
              <Heart onClickAddHearted={onClickAddHearted} onClickRemoveHearted={onClickRemoveHearted} />
            </HeartWrap>
            <CarouselWrap>
              <CarouselHotelImages items={hotelImages} />
            </CarouselWrap>
            <DetailsWrap simple={simple}>
              <LinkIntercept to={slug} target={'_blank'} config={{ skipOnClickHandler: true }}>
                <Headline>{hotelName}</Headline>
                <Subheader>
                  <HotelStars>
                    <Stars stars={hotelStars} />
                  </HotelStars>
                </Subheader>
                {!!hotelScore && (
                  <HotelScore price={!!hotelPrice}>
                    <Score score={hotelScore} />
                  </HotelScore>
                )}
                {hotelPrice && <MapMarkerHotelPrice hotelPrice={hotelPrice} packageDeparture={departure} />}
              </LinkIntercept>
              {onClickViewInList && (
                <ViewInListWrap onClick={onClickViewInList}>
                  <Arrow>
                    <FontAwesomeIcon icon='chevron-left' />
                  </Arrow>
                  <ViewInList>
                    <FormattedMessage
                      id='searchResultsMap.component.markerPopup.viewInList.label'
                      description='View in list'
                      defaultMessage='View in list'
                    />
                  </ViewInList>
                </ViewInListWrap>
              )}
            </DetailsWrap>
            {dates && <Dates dates={dates} isFlexWrapActivated={true} />}
          </Root>
        </IntlProvider>
      </ThemeProvider>
    </Provider>
  );
}

MapMarkerPopupComponent.propTypes = propTypes;

export default MapMarkerPopupComponent;

const Arrow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Root = styled.div`
  ${flexbox({
    flexDirection: 'column',
  })}

  position: relative;
  width: 304px;
  min-height: '308px';
  background-color: ${({ theme }) => theme.colors.background.light};
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 0 40px -10px #2e2e2e;
`;

const HeartWrap = styled.div`
  position: absolute;
  top: 16px;
  right: 14px;
  width: 19px;
  z-index: 2;
`;

const CarouselWrap = styled.div`
  width: 100%;
  height: '208px';
`;

const DetailsWrap = styled.div`
  position: relative;
  padding: ${({ simple }) => (simple ? '14px' : ' 14px 15px 44px')};
  width: 100%;
  min-height: ${({ simple }) => (simple ? 'unset' : '100px')};
  background-color: ${({ theme }) => theme.colors.background.light};
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
`;

const Headline = styled.h2`
  font-size: ${({ simple }) => (simple ? '18px' : '20px')};
  font-weight: bold;
  color: ${({ theme }) => theme.colors.secondary.default};
`;

const Subheader = styled.div`
  ${flexbox({
    alignItems: 'center',
  })}
  padding: 6px 0;
`;

const HotelStars = styled.div``;

const HotelScore = styled.div`
  position: absolute;
  right: 8px;
  top: -2.8rem;
`;

const ViewInListWrap = styled.button`
  position: absolute;
  bottom: 14px;
  left: 14px;
  display: flex;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.background.light};
  border: none;
  cursor: pointer;
  box-shadow: 0px 0px 0px 2px rgba(0, 0, 0, 0.2);
  border-radius: 20px;
`;

const ViewInList = styled.div`
  position: relative;
  padding: 3px 10px;
  font-size: 14px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.primary.dark};
`;
